import RADApplication from '@/Management/RADApplication';

export default class StorageHelper {

    /**
     * Der Name des Properties im Storage unter dem die Werte gespeichert werden
     */
    private static LOCAL_STORAGE_PROPERTY_NAME = 'radStorage_';
    private static LOCAL_MACHINE_STORAGE_PROPERTY_NAME = StorageHelper.LOCAL_STORAGE_PROPERTY_NAME + 'machine_global';
    private bindings: { [key: string]: KnockoutObservable<any> };
    private bindingsGlobal: { [key: string]: KnockoutObservable<any> };

    private subscriptions: { [key: string]: KnockoutSubscription };
    private subscriptionsGlobal: { [key: string]: KnockoutSubscription };

    private LOCAL_STORAGE_SUPPORTED;

    private static instance: StorageHelper;

    private constructor() {
        this.bindings = {};
        this.bindingsGlobal = {};
        this.subscriptions = {};
        this.subscriptionsGlobal = {};
        this.LOCAL_STORAGE_SUPPORTED = true;
    }

    private static getInstance() {
        if (StorageHelper.instance == null)
            StorageHelper.instance = new StorageHelper();

        return StorageHelper.instance;
    }

    /**
     * Methode zum Auslesen eines Items aus dem Storage
     */
    private getItemByName(name) {
        let _storage = StorageHelper.getInstance().getStorage();
        return _storage[name];
    }

    /**
     * Methode zum Auslesen eines Items aus dem Machine-Storage
     */
    private getMachineItemByName(name) {
        let _storage = StorageHelper.getInstance().getStorageMachine();
        return _storage[name];
    }

    /**
     * Methode zum Erstellen oder Bearbeitens eines Items im Storage
     */
    private createOrUpdateItem(name, value, notify) {
        let helper = StorageHelper.getInstance();
        if (notify == null || notify === true) {
            let bindingForName = helper.getBinding(name);

            if (bindingForName && (JSON.stringify(bindingForName()) !== JSON.stringify(value))) {
                bindingForName(value);
            }
        }

        let _storage = helper.getStorage();

        if (_.isObject(value) || _storage[name] !== value) {
            _storage[name] = value;
            helper.setStorage(_storage);
        }
    }

    /**
     * Methode zum Erstellen oder Bearbeitens eines Items im Machine-Storage
     */
    private createOrUpdateMachineItem(name, value, notify) {
        let helper = StorageHelper.getInstance();
        if (notify == null || notify === true) {
            let bindingForName = helper.getGlobalBinding(name);

            if (bindingForName) {
                bindingForName(value);
            }
        }

        let _storage = helper.getStorageMachine();

        if (_.isObject(value) || _storage[name] !== value) {
            _storage[name] = value;
            helper.setStorageMachine(_storage);
        }
    }


    /**
     * Prüft, ob der LocalStorage aktualisiert werden muss
     */
    public static checkLocalStorageRefresh() {
        let helper = StorageHelper.getInstance();

        if (!helper.LOCAL_STORAGE_SUPPORTED) return false;

        let result = false;
        let savedPdApplicationChanged = window.localStorage.getItem(StorageHelper.LOCAL_STORAGE_PROPERTY_NAME + 'PDApplicationChanged');
        let currentPdApplicationchanged = RADApplication.getInstance().pdApplicationChangedTime();

        if (savedPdApplicationChanged != null && currentPdApplicationchanged !== savedPdApplicationChanged) {
            window.localStorage.clear();
            result = true;
        }

        window.localStorage.setItem(StorageHelper.LOCAL_STORAGE_PROPERTY_NAME + 'PDApplicationChanged', currentPdApplicationchanged);
        return result;
    }

    /**
     * Liefert den Wert eines Items anhand des Namen
     */
    public static get(name: string) {
        return StorageHelper.getInstance().getItemByName(name);
    }

    public static getGlobal(name: string) {
        return StorageHelper.getInstance().getMachineItemByName(name);
    }

    /**
     * Setzt den Wert eines Items anhand des Namen
     */
    public static set(name: string, value, notify?: boolean) {
        StorageHelper.getInstance().createOrUpdateItem(name, value, notify);
    }

    public static setGlobal(name: string, value, notify?: boolean) {
        StorageHelper.getInstance().createOrUpdateMachineItem(name, value, notify);
    }

    /**
     * Wechselt einen Wert
     */
    public static toggle(name: string) {
        StorageHelper.set(name, !(StorageHelper.get(name) || false));
    }

    public static toggleGlobal(name: string) {
        StorageHelper.setGlobal(name, !(StorageHelper.getGlobal(name) || false));
    }

    public static remove(name: string): void {
        const helper = StorageHelper.getInstance();

        delete helper.bindings[name];

        const subscription = helper.subscriptions[name];
        if (subscription != null)
            subscription.dispose();
        delete helper.subscriptions[name];

        let _storage = helper.getStorage();

        if (_storage.hasOwnProperty(name)) {
            delete _storage[name];
            helper.setStorage(_storage);
        }
    }

    public static removeGlobal(name: string): void {
        const helper = StorageHelper.getInstance();

        delete helper.bindingsGlobal[name];

        const subscription = helper.subscriptionsGlobal[name];
        if (subscription != null)
            subscription.dispose();
        delete helper.subscriptionsGlobal[name];

        let _storage = helper.getStorageMachine();

        if (_storage.hasOwnProperty(name)) {
            delete _storage[name];
            helper.setStorageMachine(_storage);
        }
    }

    public static addStorageChangeEventHandler(fn: any) : void {
        window.addEventListener('storage', fn, false);
    }

    public static isKeySet(key: string): boolean {
        const helper = StorageHelper.getInstance();

        const _storage = helper.getStorage();
        return !String.isNullOrEmpty(_storage[key]);
    }

    public static isGlobalKeySet(key: string): boolean {
        const helper = StorageHelper.getInstance();

        const _storage = helper.getStorageMachine();
        return !String.isNullOrEmpty(_storage[key]);
    }

    /**
     * Fügt ein Binding hinzu
     */
    private addBinding(name, value) {
        let helper = StorageHelper.getInstance();

        if (_.isArray(value)) {
            helper.bindings[name] = ko.observableArray(value);
        }
        else {
            helper.bindings[name] = ko.observable(value);
        }

        const subscription = helper.bindings[name].subscribe((value) => {
            StorageHelper.set(name, value, true);
        });

        this.subscriptions[name] = subscription;

        return helper.bindings[name];
    }

    private addGlobalBinding(name, value) {
        if (_.isArray(value)) {
            this.bindingsGlobal[name] = ko.observableArray(value);
        } else {
            this.bindingsGlobal[name] = ko.observable(value);
        }

        const subscription = this.bindingsGlobal[name].subscribe((value) => {
            StorageHelper.setGlobal(name, value, true);
        });

        this.subscriptionsGlobal[name] = subscription;

        return this.bindingsGlobal[name];
    }

    /**
     * Liefert alle Bindings zu einem Namen
     */
    private getBinding(name) {
        return this.bindings[name] || null;
    }

    private getGlobalBinding(name) {
        return this.bindingsGlobal[name] || null;
    }

    /**
     * Liefert ein Observable zum Lesen und Schreiben eines Wertes
     * Achtung: Wenn der 3. Parameter eine Funktion ist und diese FALSE returned, wird der defaultValue zurückgegeben (als Computed)
     * @param name
     * @param defaultValue
     * @param overrideValueORconditionalEvaluator
     */
    public static bind<T>(name: string, defaultValue: T, overrideValueORconditionalEvaluator?: (...params) => boolean): KnockoutComputed<T> {
        let helper = StorageHelper.getInstance();

        let value = this.get(name);
        let overrideValue = overrideValueORconditionalEvaluator != null && !_.isFunction(overrideValueORconditionalEvaluator) ? overrideValueORconditionalEvaluator : null;
        let conditionalEvaluator = overrideValueORconditionalEvaluator != null && _.isFunction(overrideValueORconditionalEvaluator) ? overrideValueORconditionalEvaluator : null;

        if (conditionalEvaluator == null || conditionalEvaluator()) {
            value = overrideValue ? overrideValue : (value != null ? value : defaultValue);

            let binding = helper.getBinding(name);

            if (binding == null) {
                binding = helper.addBinding(name, value);
            }

            return ko.computed({
                read: () => {
                    return binding();
                },
                write: (newVal) => {
                    binding(newVal);
                }
            });
        }
        else {
            const observable = ko.observable(defaultValue);
            return ko.computed({
                read: () => {
                    return observable();
                },
                write: (newVal) => {
                    observable(newVal);
                }
            });
        }
    }

    /**
     * Achtung: Wenn der 3. Parameter eine Funktion ist und diese FALSE returned, wird der defaultValue direkt zurückgegeben und ist somit nicht automatisch ein Computed!!
     * @param name
     * @param defaultValue
     * @param overrideValueORconditionalEvaluator 
     */
    public static bindGlobal(name: string, defaultValue, overrideValueORconditionalEvaluator?: Function | any) {
        let helper = StorageHelper.getInstance();

        let value = this.getGlobal(name);
        let overrideValue = overrideValueORconditionalEvaluator != null && !_.isFunction(overrideValueORconditionalEvaluator) ? overrideValueORconditionalEvaluator : null;
        let conditionalEvaluator = overrideValueORconditionalEvaluator != null && _.isFunction(overrideValueORconditionalEvaluator) ? overrideValueORconditionalEvaluator : null;

        if (conditionalEvaluator == null || conditionalEvaluator()) {
            value = overrideValue ? overrideValue : (value != null ? value : defaultValue);

            let binding = helper.getGlobalBinding(name);

            if (binding == null) {
                binding = helper.addGlobalBinding(name, value);
            }

            return binding;
        }
        else {
            return defaultValue;
        }
    }

    private getStorage(): {[key: string]: any} {
        const json = window.localStorage.getItem(StorageHelper.LOCAL_STORAGE_PROPERTY_NAME + RADApplication.getInstance().user.pdoId());

        if (json != null) {
            return JSON.parse(json);
        } else {
            return {};
        }
    }

    private setStorage(storage) {
        try {
            const json = JSON.stringify(storage);
            window.localStorage.setItem(StorageHelper.LOCAL_STORAGE_PROPERTY_NAME + RADApplication.getInstance().user.pdoId(), json);
        } catch (e) {
            //TODO if the local-storage runs over, we need to handle the 'QuotaExceededError' (approx. 5MB)
        }
    }

    private getStorageMachine() {
        const json = window.localStorage.getItem(StorageHelper.LOCAL_MACHINE_STORAGE_PROPERTY_NAME);

        if (json != null) {
            return JSON.parse(json);
        } else {
            return {};
        }
    }

    private setStorageMachine(storageMachine) {
        const json = JSON.stringify(storageMachine);
        window.localStorage.setItem(StorageHelper.LOCAL_MACHINE_STORAGE_PROPERTY_NAME, json);
    }
}